import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import AdminNavBar from "../components/AdminNavBar";
import { AdminPageBody, AdminLoadingState, AdminEmptyState } from "../components/AdminPageShell";
import { useAdminNews } from "../store/admin";
import { formatDate, statusBadgeClass } from "./components/news/newsUtils";

// ===== ADMIN NEWS DETAIL — GET /api/admin/news/:id =====
const NewsDetail = ({
  useNewsStore = useAdminNews,
  NavbarComponent = AdminNavBar,
  backPath = "/admin/news-management",
} = {}) => {
  const { newsId } = useParams();
  const navigate = useNavigate();
  const {
    newsDetail,
    newsDetailLoading,
    newsDetailError,
    fetchNewsById,
  } = useNewsStore();

  useEffect(() => {
    if (newsId) fetchNewsById(newsId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsId]);

  const detail = newsDetail;
  const status = String(detail?.status || "DRAFT").toUpperCase();
  const cover = detail?.coverImage || detail?.cover_image || null;

  return (
    <div>
      <NavbarComponent
        title="News Management"
        subTitle={detail?.title || "Article"}
        context="Review the full article before publishing or editing it."
      />
      <AdminPageBody>
        <button
          type="button"
          onClick={() => navigate(backPath)}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-medium text-slate-600 shadow-sm transition hover:bg-slate-50"
        >
          <ArrowLeft className="w-4 h-4" /> Back to News Management
        </button>

        {newsDetailError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600">
            {newsDetailError}
          </div>
        ) : null}

        {newsDetailLoading || (!detail && !newsDetailError) ? (
          <AdminLoadingState />
        ) : !detail ? (
          <AdminEmptyState title="Article not found" description="It may have been deleted." />
        ) : (
          <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            {cover ? (
              <img src={cover} alt={detail.coverImageAlt || detail.title} className="h-64 w-full object-cover" />
            ) : null}
            <div className="space-y-5 p-6">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold ${statusBadgeClass(status)}`}>
                  {status}
                </span>
                {detail.featured ? (
                  <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-bold text-amber-700">
                    Featured
                  </span>
                ) : null}
                <span className="text-xs text-slate-500">
                  Updated {formatDate(detail.updatedAt || detail.createdAt)}
                </span>
              </div>

              <div>
                <h1 className="m-0 text-2xl font-bold text-slate-900">{detail.title || "Untitled"}</h1>
                {detail.excerpt ? (
                  <p className="m-0 mt-2 text-base text-slate-600">{detail.excerpt}</p>
                ) : null}
              </div>

              <div className="grid gap-3 border-y border-slate-100 py-4 text-sm sm:grid-cols-3">
                <div>
                  <p className="m-0 text-xs font-semibold uppercase tracking-wide text-slate-500">Category</p>
                  <p className="m-0 mt-1 text-slate-800">{detail.category?.name || detail.categoryName || detail.categoryId || "—"}</p>
                </div>
                <div>
                  <p className="m-0 text-xs font-semibold uppercase tracking-wide text-slate-500">Published at</p>
                  <p className="m-0 mt-1 text-slate-800">{formatDate(detail.publishAt)}</p>
                </div>
                <div>
                  <p className="m-0 text-xs font-semibold uppercase tracking-wide text-slate-500">Tags</p>
                  <p className="m-0 mt-1 text-slate-800">
                    {Array.isArray(detail.tags) && detail.tags.length ? detail.tags.join(", ") : "—"}
                  </p>
                </div>
              </div>

              <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
                {detail.content || "No content."}
              </div>

              {detail.seoTitle || detail.seoDescription ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
                  <p className="m-0 text-xs font-semibold uppercase tracking-wide text-slate-500">SEO</p>
                  <p className="m-0 mt-1 font-semibold text-slate-800">{detail.seoTitle || "—"}</p>
                  <p className="m-0 mt-1 text-slate-600">{detail.seoDescription || "—"}</p>
                </div>
              ) : null}
            </div>
          </article>
        )}
      </AdminPageBody>
    </div>
  );
};

export default NewsDetail;
